<script setup lang="ts">
import { explainSkillAffix, getSkillAffixClass, getSkillName } from '@/core/skill'
import { getSupremeBasicAttrValue } from '@/core/equip/supreme'
import { BasicAttrTextEnum } from '@/enums'
import { useUserStore } from '@/stores/modules/user'
import type { SkillItemType } from '@/types/skill'
import type { UserAttrType } from '@/types/user'
import { computed, ref } from 'vue'
import { formatNumberWithCommas } from '@/utils'
import { socketEmitAsync } from '@/utils/web-socket'
import { noPercentEquipAttrKeyList } from '@/views/item/data'
import { getRemoldPrice } from '../data'
import { DArrowLeft, QuestionFilled } from '@element-plus/icons-vue'

const userStore = useUserStore()

const skillItemId = ref('')
const skillInfo = computed(() => {
  return userStore.skillList.find((item) => item.id === skillItemId.value)
})
const subSkillItemId = ref('')
const subSkillInfo = computed(() => {
  return userStore.skillList.find((item) => item.id === subSkillItemId.value)
})

const skillEquippedList = computed(() => {
  return userStore.skillEquippedList.map((id) => userStore.skillList.find((item) => item.id === id))
})
const skillItemList = computed(() => {
  const items = userStore.skillList
  if (userStore.skillList.length === 0) return []
  return items.filter((item) => {
    const equippedIdList = userStore.skillEquippedList
    if (['normal', 'race'].includes(item.quality)) return false
    if (skillInfo.value) {
      if (item.quality !== skillInfo.value.quality) return false
      if (item.skillId !== skillInfo.value.skillId) return false
      if (item.id === skillInfo.value.id) return false
    }
    return !equippedIdList.includes(item.id)
  })
})

const hadRecord = ref(false)
function handleChoice(type: 'main' | 'sub' | 'all', item: SkillItemType | undefined) {
  if (type === 'all') {
    hadRecord.value = false
    skillItemId.value = item?.id || ''
    subSkillItemId.value = item?.id || ''
  } else if (type === 'main') {
    hadRecord.value = false
    skillItemId.value = item?.id || ''
    socketEmitAsync({ type: 'checkRemoldRecord', data: { itemId: skillItemId.value } }).then((res) => {
      if (res) {
        hadRecord.value = true
      }
    })
  } else {
    console.log(hadRecord.value)

    if (hadRecord.value) return ElMessage.warning('已存在重塑记录，请先确认重塑结果')
    subSkillItemId.value = item?.id || ''
  }
}

const remoldResShow = ref(false)
const remoldAttrs = ref<SkillItemType['attrs']>([])
const oldAttrs = ref<SkillItemType['attrs']>([])
const keepAttrIndexList = ref<number[]>([])
const isSubmitting = ref(false)

const maxKeepCount = computed(() => {
  return Math.floor(remoldAttrs.value.length / 2)
})
const keepCount = computed(() => keepAttrIndexList.value.length)
const minValue = computed(() => {
  if (!skillInfo.value) return 0
  const remoldCount = skillInfo.value.remoldCount || 0
  return Math.min((remoldCount + 1) * 0.5, 10)
})

function handleAttrChoice(index: number, val: 'keep' | 'cover') {
  if (val !== 'keep' && val !== 'cover') return
  if (val === 'keep') {
    if (keepAttrIndexList.value.length >= maxKeepCount.value) {
      ElMessage.warning(`最多只能保留${maxKeepCount.value}条词条`)
      return
    }
    keepAttrIndexList.value.push(index)
  } else {
    keepAttrIndexList.value = keepAttrIndexList.value.filter((i) => i !== index)
  }
}

async function handleRemold() {
  if (!skillInfo.value) return
  const mainId = skillInfo.value.id
  const subId = subSkillInfo.value?.id
  const needDiamond = getRemoldPrice((skillInfo.value.remoldCount || 0) + 1)
  if (!hadRecord.value && userStore.diamond < needDiamond) return ElMessage.warning('您的钻石不足')

  ElMessageBox.confirm('<p class="affix">确定开始重塑吗？该操作将<span class="shrink">销毁</span>材料技能，并在确认结果后生效词条。</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const { remoldAttrs: resRemoldAttrs } = await socketEmitAsync<{ remoldAttrs: SkillItemType['attrs'] }>({
        type: 'remoldSkill',
        data: { mainId, subId },
      })

      oldAttrs.value = [...(skillInfo.value?.attrs || [])]
      remoldAttrs.value = resRemoldAttrs
      keepAttrIndexList.value = []

      const mainSkill = userStore.skillList.find((item) => item.id === mainId)
      if (mainSkill) {
        mainSkill.remoldCount = (mainSkill.remoldCount || 0) + 1
      }
      userStore.skillList = userStore.skillList.filter((item) => item.id !== subId)
      subSkillItemId.value = ''
      userStore.diamond -= needDiamond

      remoldResShow.value = true
      ElMessage.success('重塑完成，请确认词条结果')
    })
    .catch(() => {})
}

async function handleConfirmRemold() {
  if (!skillInfo.value || !remoldAttrs.value.length || isSubmitting.value) return

  ElMessageBox.confirm('<p class="affix">确认词条选择结果吗？该操作将<span class="shrink">无法撤销</span>。</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
    beforeClose: async (action, instance, done) => {
      if (action !== 'confirm') return done()
      if (!skillInfo.value) return done()
      try {
        isSubmitting.value = true
        instance.confirmButtonLoading = true
        instance.confirmButtonText = '提交中...'

        const userAttr = await socketEmitAsync<UserAttrType>({
          type: 'chooseRemoldRes',
          data: { mainId: skillInfo.value.id, keepAttrIndexList: keepAttrIndexList.value },
        })
        const mainSkill = userStore.skillList.find((item) => item.id === skillInfo.value?.id)
        if (mainSkill) {
          mainSkill.attrs = remoldAttrs.value.map((item, index) => {
            if (keepAttrIndexList.value.includes(index)) return oldAttrs.value[index]
            return item
          })
        }
        userStore.userAttr = userAttr

        remoldResShow.value = false
        remoldAttrs.value = []
        oldAttrs.value = []
        keepAttrIndexList.value = []
        hadRecord.value = false
        ElMessage.success('重塑结果确认成功')
        done()
      } finally {
        isSubmitting.value = false
        instance.confirmButtonLoading = false
        instance.confirmButtonText = '确定'
      }
    },
  })
}
</script>

<template>
  <div class="remold">
    <div class="remold-table border-wrap">
      <h4>重塑台</h4>
      <div class="remold-skill">
        <CommonBtn size="small" style="width: 100%" @done="handleChoice('all', undefined)">
          <span v-if="skillInfo" :class="skillInfo.quality" v-html="getSkillName(skillInfo)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div style="text-align: center">
        <el-icon size="25" style="transform: rotate(90deg)"><DArrowLeft /></el-icon>
      </div>
      <div class="remold-skill">
        <CommonBtn size="small" style="width: 100%" @done="handleChoice('sub', undefined)">
          <span v-if="subSkillInfo" :class="subSkillInfo.quality" v-html="getSkillName(subSkillInfo)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>重塑说明</h4>
            <p>1. 通过<span class="info">重塑</span>，可重新随机技能的词条。</p>
            <p>2. 重塑需消耗<span class="info">同名、同品质</span>的技能书，以及随重塑次数增加愈加昂贵的<span class="diamond">钻石</span>。</p>
            <p>3. 重塑后，您可以选择<span class="info">保留</span>一部分词条（保留数量为技能书词条数量的一半，向下取整）。</p>
            <p>4. 随着重塑次数的增加，技能书的<span class="info">保底分值</span>也会<span class="grow">提高</span>。</p>
            <p>
              5. 进行<span class="grow">20</span>次重塑后，技能词条分值必定为<span class="peerless">最大值</span>，且重塑消耗降低至
              <span class="diamond">5000</span>钻石
            </p>
            <p>6. 重塑词条区间表格所示：</p>
            <table border style="width: 100%; text-align: center">
              <tr>
                <th>词条位置</th>
                <th>词条结果</th>
              </tr>
              <tr>
                <td>1</td>
                <td>种族伤害加成、全伤害加成</td>
              </tr>
              <tr>
                <td>2</td>
                <td>元素伤害加成、冷却缩减、法耗缩减</td>
              </tr>
              <tr>
                <td>3</td>
                <td>特殊属性</td>
              </tr>
              <tr>
                <td>4</td>
                <td>种族伤害加成、全伤害加成</td>
              </tr>
              <tr>
                <td>5</td>
                <td>特殊属性</td>
              </tr>
              <tr>
                <td>6</td>
                <td>元素伤害加成、冷却缩减、法耗缩减、全伤害加成</td>
              </tr>
              <tr>
                <td>7</td>
                <td>特殊属性</td>
              </tr>
            </table>
          </div>
        </el-popover>
      </div>
      <template v-if="(skillInfo && subSkillInfo) || hadRecord">
        <div class="info-card">
          <p>
            <span>重塑次数：{{ skillInfo?.remoldCount || 0 }}</span>
            <span
              >保底分值：<span :class="getSkillAffixClass(minValue)">{{ minValue * 10 }}%</span></span
            >
          </p>
          <p>重塑花费：{{ hadRecord ? 0 : getRemoldPrice((skillInfo?.remoldCount || 0) + 1) }} 钻石</p>
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleRemold">{{ hadRecord ? '确认结果' : '开始重塑' }}</el-button>
        </div>
      </template>
    </div>
    <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px" v-show="!skillInfo">
      <h4>已携带</h4>
      <div class="item-btn-wrap btn-wrap">
        <el-row :gutter="10">
          <el-col :span="12" v-for="(item, index) of skillEquippedList" :key="index">
            <CommonBtn
              size="small"
              @done="handleChoice('main', item)"
              :style="{ borderColor: item?.id && skillInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" :class="item.quality" v-html="getSkillName(item)"></span>
              <span v-else> (未携带)</span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="item-list border-wrap package">
      <h4>{{ skillInfo ? '选择技能作为材料' : '背包' }}</h4>
      <p class="cur-gold">💎 {{ formatNumberWithCommas(userStore.diamond) }}</p>
      <div class="item-btn-wrap btn-wrap item-list-wrap" v-show="skillItemList.length">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of skillItemList" :key="item.id">
            <CommonBtn
              size="small"
              @done="handleChoice(!skillInfo ? 'main' : 'sub', item)"
              :style="{ borderColor: item?.id && subSkillInfo?.id === item.id ? 'red' : '' }"
            >
              <span :class="item.quality" v-html="getSkillName(item)"></span>
            </CommonBtn>
          </el-col>
        </el-row>
      </div>
      <p class="affix" v-show="skillInfo && !skillItemList.length" style="padding: 0 5px; text-align: center">
        您背包中不存在<span class="info">同品质且同名</span>的技能
      </p>
    </div>

    <el-dialog v-model="remoldResShow" width="100%" style="max-width: 720px" :before-close="() => false" :show-close="false">
      <h2 class="affix" style="text-align: center; margin-bottom: 5px">
        <span class="grow">{{ hadRecord ? '请确认重塑结果' : '重塑完成，请确认结果' }}</span>
      </h2>
      <p class="affix" style="text-align: center; margin-top: 0">
        已还原 <span class="info">{{ keepCount }}</span> / {{ maxKeepCount }} 条词条
      </p>
      <div class="remold-result" v-if="skillInfo && remoldAttrs.length">
        <div class="result-head">
          <span>重塑前</span>
          <span>操作</span>
          <span>重塑后</span>
        </div>
        <div class="result-row" v-for="(attr, index) of remoldAttrs" :key="index">
          <div class="before">
            <template v-if="oldAttrs[index]?.basic">
              <p
                v-for="(val, key) of oldAttrs[index].basic"
                :key="key"
                :class="[getSkillAffixClass(val as number), !keepAttrIndexList.includes(index) ? 'del' : '']"
              >
                <span>{{ BasicAttrTextEnum[key] }}</span>
                <span>
                  +
                  <span>{{ getSupremeBasicAttrValue(key, val as number) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
              </p>
            </template>
            <template v-else-if="oldAttrs[index].special">
              <p
                class="affix"
                :class="!keepAttrIndexList.includes(index) ? 'del' : ''"
                v-for="(item, key) of explainSkillAffix([oldAttrs[index].special], false)"
                :key="key"
                v-html="item"
              ></p>
            </template>
          </div>
          <div class="action">
            <el-button size="small" plain type="primary" @click="handleAttrChoice(index, 'keep')" v-show="!keepAttrIndexList.includes(index)"
              >保留</el-button
            >
            <el-button size="small" plain type="warning" @click="handleAttrChoice(index, 'cover')" v-show="keepAttrIndexList.includes(index)"
              >覆盖</el-button
            >
          </div>
          <div class="after">
            <template v-if="attr?.basic">
              <p
                v-for="(val, key) of attr.basic"
                :key="key"
                :class="[getSkillAffixClass(val as number), keepAttrIndexList.includes(index) ? 'del' : '']"
              >
                <span>{{ BasicAttrTextEnum[key] }}</span>
                <span>
                  +
                  <span>{{ getSupremeBasicAttrValue(key, val as number) }}{{ noPercentEquipAttrKeyList.includes(key) ? '' : '%' }}</span>
                </span>
              </p>
            </template>
            <template v-else-if="attr.special">
              <p
                class="affix"
                :class="keepAttrIndexList.includes(index) ? 'del' : ''"
                v-for="(item, key) of explainSkillAffix([attr.special], false)"
                :key="key"
                v-html="item"
              ></p>
            </template>
          </div>
        </div>
      </div>
      <template #footer>
        <p style="text-align: center; margin-bottom: 0">
          <el-button size="small" plain @click="handleConfirmRemold" type="success" :loading="isSubmitting">确认结果</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.remold {
  display: flex;
  flex-flow: column nowrap;
  overflow: hidden;
  .remold-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .remold-skill {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
    .info-card p {
      margin: 6px 0;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .el-button + .el-button {
    margin-left: 0;
  }

  .remold-result {
    max-height: 56vh;
    overflow: auto;
    .result-head {
      display: flex;
      justify-content: space-between;
      padding: 0 10px;
      margin-bottom: 10px;
    }
    .result-row {
      display: flex;
      .before,
      .after {
        width: 50%;
        p.del {
          text-decoration: line-through;
          opacity: 0.3;
        }
      }
      .after {
        p {
          text-align: right;
        }
      }
      .action {
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 5px;
      }
    }
  }
}
</style>
